import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { PageSizeOption, UserOrder, UserOrderData } from '@models/dashboard.model';
import { ClientSettings } from '@models/shopping.model';
import {
  NgbDropdownModule,
  NgbHighlight,
  NgbPaginationModule,
  NgbTooltipModule
} from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { DashboardService } from '@services/dashboard/dashboard.service';
import { SessionService } from '@services/session/session.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { Subject, Subscription, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs';
import { DataLoaderDirective } from 'tangoe-design-system';

const page_sizes: PageSizeOption[] = [
  {
    value: 10,
    name: '10 Items Per Page'
  },
  {
    value: 25,
    name: '25 Items Per Page'
  },
  {
    value: 50,
    name: '50 Items Per Page'
  },
  {
    value: 100,
    name: '100 Items Per Page'
  }
];

@Component({
  selector: 'ts-my-orders',
  templateUrl: './my-orders.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    RouterModule,
    FormsModule,
    NgbDropdownModule,
    NgbHighlight,
    NgbPaginationModule,
    TranslationPipe,
    DataLoaderDirective
  ]
})
export class MyOrdersComponent implements OnInit, OnDestroy {
  my_orders: UserOrder[] = [];
  my_orders_total_count: number;
  my_orders_actual_count: number;
  current_page: number = 1;
  page_size: number = 10;
  page_size_options: PageSizeOption[] = page_sizes;
  search_term: FormControl = new FormControl('', { nonNullable: true });
  my_order_filters: FormGroup = new FormGroup({
    myself: new FormControl(null),
    someone_else: new FormControl(null)
  });
  cc_settings_enabled: boolean = false;
  is_tmobile_environment: boolean = false;
  private subscriptions: Subscription = new Subscription();
  private order_search: Subject<string> = new Subject();

  constructor(
    private dashboard_svc: DashboardService,
    private session_svc: SessionService,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {}

  ngOnInit(): void {
    this.page_size_options.forEach((size_option: PageSizeOption) => {
      size_option.name = size_option.name.replace(
        'Items Per Page',
        this.translation_svc.translate('items_per_page') ?? size_option.name
      );
    });

    this.subscriptions.add(
      this.shopping_svc.clientSettings('user-orders').subscribe({
        next: (res: ClientSettings) => {
          this.cc_settings_enabled = res.cc_settings_enabled;
        },
        error: (err: HttpErrorResponse) => {}
      })
    );

    this.subscriptions.add(this.getUserOrders());

    this.subscriptions.add(
      this.search_term.valueChanges.pipe(distinctUntilChanged()).subscribe((e: string) => {
        if (!this.search_term.value || this.current_page > 1) this.current_page = 1;
        if (e.length > 1 || e.length === 0) this.order_search.next(e);
      })
    );

    this.authorized_to_ofo &&
      this.subscriptions.add(
        this.my_order_filters.valueChanges.subscribe(
          (e: { myself: boolean; someone_else: boolean }) => {
            this.current_page = 1;
            this.order_search.next(this.search_term.value);
          }
        )
      );

    this.order_search
      .pipe(
        debounceTime(300),
        tap((term: string) => {
          if (!term) this.current_page = 1;
        }),
        switchMap((term: string) => {
          return this.dashboard_svc.userOrders(
            term,
            this.my_order_filters.dirty
              ? this.calculateOrderFilterParamValue(this.my_order_filters.value)
              : undefined,
            this.page_size,
            this.calculateOffset()
          );
        })
      )
      .subscribe((res: UserOrderData) => this.setUserOrders(res));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get active_filters(): string[] {
    return Object.keys(this.my_order_filters.controls).filter(
      (key: string) => this.my_order_filters.controls[key].value
    );
  }

  get authorized_to_ofo(): boolean {
    return this.session_svc.getAuthorizedToOfo();
  }

  get ngb_highlight_term(): string {
    return this.search_term.value.length > 1 ? this.search_term.value : '';
  }

  getUserOrders(search_term?: string): void {
    this.dashboard_svc
      .userOrders(
        search_term,
        this.calculateOrderFilterParamValue(this.my_order_filters.value),
        this.page_size,
        this.calculateOffset()
      )
      .subscribe((res: UserOrderData) => this.setUserOrders(res));
  }

  setUserOrders(orders: UserOrderData): void {
    // the logic in the next line of code will eventually be moved to BE once Admin work is implemented
    orders.items.map(
      (order: UserOrder) =>
        (order.myself = order.order_recipient.id === this.session_svc.getUser()?.id)
    );

    this.is_tmobile_environment = orders.items[0]?.tmobile_environment ?? false;
    this.my_orders = orders.items;
    this.my_orders_total_count = orders.meta.total_count;
    this.my_orders_actual_count = orders.meta.actual_count;
  }

  calculateTableIndexLabel(): string {
    let label: string = '';

    if (this.my_orders_total_count === 0) {
      label = '0 - 0';
    } else if (this.my_orders_total_count < this.page_size * this.current_page) {
      label = `${this.page_size * this.current_page - this.page_size + 1} - ${
        this.my_orders_total_count
      }`;
    } else {
      label = `${this.page_size * this.current_page - this.page_size + 1} - ${
        this.page_size * this.current_page
      }`;
    }

    return label + ` / ${this.my_orders_total_count} ${this.translation_svc.translate('results')}`;
  }

  private calculateOrderFilterParamValue(e: {
    myself: boolean;
    someone_else: boolean;
  }): string | undefined {
    if (e.myself && e.someone_else) return undefined;
    else if (e.myself && !e.someone_else) return 'myself';
    else if (e.someone_else && !e.myself) return 'other';
    else return undefined;
  }

  private calculateOffset(): number {
    return this.current_page > 1 ? this.page_size * (this.current_page - 1) : 0;
  }
}
